import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface ModelHealth {
  model: string
  status: string
  uptime: number
  latency_ms: number
  errors_pct: number
  drift_score: number
}

interface InfraMetric {
  resource: string
  usage: number
  cost_daily: number
  trend: string
  change: number
}

interface Alert {
  id: string
  type: string
  model: string
  severity: string
  message: string
  timestamp: string
}

interface Statistics {
  total_models: number
  models_in_production: number
  total_predictions_today: number
  infrastructure_cost_mtd: number
  ml_team_velocity: number
  overall_health_score: number
}

export default function MLOpsDashboardPage() {
  const [health, setHealth] = useState<ModelHealth[]>([])
  const [infrastructure, setInfrastructure] = useState<InfraMetric[]>([])
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [healthRes, infraRes, alertsRes, statsRes] = await Promise.all([
        fetch('/api/mlopsdashboard/health'),
        fetch('/api/mlopsdashboard/infrastructure'),
        fetch('/api/mlopsdashboard/alerts'),
        fetch('/api/mlopsdashboard/statistics')
      ])
      if (healthRes.ok) setHealth(await healthRes.json())
      if (infraRes.ok) setInfrastructure(await infraRes.json())
      if (alertsRes.ok) setAlerts(await alertsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching MLOps dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const getStatusBadge = (status: string) => {
    if (status === 'Healthy') return 'bg-green-100 text-green-800'
    if (status === 'Warning') return 'bg-yellow-100 text-yellow-800'
    if (status === 'Degraded') return 'bg-orange-100 text-orange-800'
    return 'bg-red-100 text-red-800'
  }

  const getSeverityBadge = (severity: string) => {
    if (severity === 'High') return 'bg-red-100 text-red-800'
    if (severity === 'Medium') return 'bg-yellow-100 text-yellow-800'
    return 'bg-blue-100 text-blue-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">MLOps Dashboard</h1>
          <p className="mt-2 text-gray-600">Phase 280 - Comprehensive MLOps overview</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Models</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_models}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">In Production</p>
              <p className="text-2xl font-bold text-green-600">{statistics.models_in_production}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Predictions Today</p>
              <p className="text-2xl font-bold text-blue-600">{formatNumber(statistics.total_predictions_today)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Infra Cost MTD</p>
              <p className="text-2xl font-bold text-orange-600">${formatNumber(statistics.infrastructure_cost_mtd)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Team Velocity</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.ml_team_velocity}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Health Score</p>
              <p className="text-2xl font-bold text-green-600">{statistics.overall_health_score}%</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Infrastructure</h2>
            </div>
            <div className="p-6 space-y-4">
              {infrastructure.map((infra, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{infra.resource}</span>
                      <span className="text-sm text-gray-500">${infra.cost_daily}/day</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full">
                      <div className={`h-2 rounded-full ${infra.usage >= 80 ? 'bg-red-500' : infra.usage >= 60 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{width: `${infra.usage}%`}}></div>
                    </div>
                  </div>
                  <span className={`ml-4 text-sm font-medium ${infra.trend === 'up' ? 'text-red-600' : 'text-green-600'}`}>
                    {infra.trend === 'up' ? '+' : ''}{infra.change}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Alerts</h2>
            </div>
            <div className="p-6 space-y-3">
              {alerts.map((alert) => (
                <div key={alert.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900">{alert.model}</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${getSeverityBadge(alert.severity)}`}>{alert.severity}</span>
                  </div>
                  <p className="text-sm text-gray-600">{alert.message}</p>
                  <p className="text-xs text-gray-400 mt-1">{alert.timestamp}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Model Health</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Uptime</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Latency</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Drift</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {health.map((h, idx) => (
                  <tr key={idx}>
                    <td className="px-6 py-4 font-medium text-gray-900">{h.model}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(h.status)}`}>{h.status}</span>
                    </td>
                    <td className="px-6 py-4 text-green-600">{h.uptime}%</td>
                    <td className="px-6 py-4 text-blue-600">{h.latency_ms}ms</td>
                    <td className="px-6 py-4">
                      <span className={h.errors_pct > 0.1 ? 'text-red-600' : 'text-green-600'}>{h.errors_pct}%</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={h.drift_score > 0.1 ? 'text-red-600' : 'text-green-600'}>{h.drift_score}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
